import { type PrivateWorkspaceAgentSessionApi } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentSession';
import { parseToolCallMetadata } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/toolCallMetadata';
import {
  isWorkspaceAgentCancelledToolResultContent,
  parseToolResultMetadata,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/toolResultMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { buildWorkspaceAgentToolCallById } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentToolCallById';
import { isWorkspaceAgentSessionRunning } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionRunning';

export type WorkspaceAgentVariantNextActionsSessionState = Pick<
  PrivateWorkspaceAgentSessionApi,
  | 'activeRunId'
  | 'isAwaitingElicitationFollowUp'
  | 'isAwaitingRunStart'
  | 'isLoadingMessages'
  | 'isLoadingSessions'
  | 'isSending'
  | 'isSessionAwaitingInput'
  | 'isSessionProcessing'
>;

export type ShouldShowWorkspaceAgentVariantNextActionsParams = {
  messages: readonly WorkspaceAgentMessage[];
  session: WorkspaceAgentVariantNextActionsSessionState;
};

const isWorkspaceAgentSessionSettledForVariantNextActions = (
  session: WorkspaceAgentVariantNextActionsSessionState,
): boolean =>
  !session.isLoadingSessions &&
  !session.isLoadingMessages &&
  !session.isSessionAwaitingInput &&
  !session.isAwaitingElicitationFollowUp &&
  !isWorkspaceAgentSessionRunning(session);

/** Session lifecycle and thread shape for the post-variant next-actions row. */
export const shouldShowWorkspaceAgentVariantNextActions = ({
  messages,
  session,
}: ShouldShowWorkspaceAgentVariantNextActionsParams): boolean =>
  didLatestTurnRunBulkVariants({
    isSessionSettled: isWorkspaceAgentSessionSettledForVariantNextActions(session),
    messages,
  });

export type DidLatestTurnRunBulkVariantsParams = {
  /**
   * Sessions list has loaded and nothing is in progress or waiting on the user. Missing status
   * before that load reads as idle, which would flash the pills on a refresh mid-run.
   */
  isSessionSettled: boolean;
  messages: readonly WorkspaceAgentMessage[];
};

const findLatestUserMessage = (messages: readonly WorkspaceAgentMessage[]): WorkspaceAgentMessage | undefined => {
  let latestUserMessage: WorkspaceAgentMessage | undefined;

  for (const message of messages) {
    if (message.role !== 'user') {
      continue;
    }

    if (!latestUserMessage || message.sequence > latestUserMessage.sequence) {
      latestUserMessage = message;
    }
  }

  return latestUserMessage;
};

const BULK_VARIANTS_TOOL_NAME = 'bulk_variants';

/** A stop mid-run persists as a tool result whose error is "Turn cancelled". */
const wasVariantTurnInterrupted = (
  messages: readonly WorkspaceAgentMessage[],
  latestUserMessage: WorkspaceAgentMessage,
): boolean =>
  messages.some(
    (message) =>
      message.role === 'toolResult' &&
      message.sequence > latestUserMessage.sequence &&
      isWorkspaceAgentCancelledToolResultContent(message.content),
  );

/**
 * Tool results persist `toolName`. When that field is missing, the paired tool-call row still has
 * the name. Either one is enough, and neither depends on how many files were saved.
 */
const didTurnUseBulkVariants = (
  messages: readonly WorkspaceAgentMessage[],
  latestUserMessage: WorkspaceAgentMessage,
): boolean => {
  const toolCallById = buildWorkspaceAgentToolCallById(messages);

  return messages.some((message) => {
    if (message.sequence <= latestUserMessage.sequence) {
      return false;
    }

    if (message.role === 'toolCall') {
      return parseToolCallMetadata(message.metadata).some((toolCall) => toolCall.name === BULK_VARIANTS_TOOL_NAME);
    }

    if (message.role !== 'toolResult') {
      return false;
    }

    const parsed = parseToolResultMetadata(message.metadata);

    if (!parsed) {
      return false;
    }

    const toolCallId = parsed.outcome === 'readable' ? parsed.metadata.toolCallId : parsed.toolCallId;
    const toolName =
      parsed.outcome === 'readable'
        ? (parsed.metadata.toolName ?? toolCallById.get(toolCallId)?.name)
        : toolCallById.get(toolCallId)?.name;

    return toolName === BULK_VARIANTS_TOOL_NAME;
  });
};

/**
 * True when the latest user turn called `bulk_variants` and the user did not stop it. Save
 * location, file type, suffix, and the structured-display icon do not matter.
 */
export const didLatestTurnRunBulkVariants = ({
  isSessionSettled,
  messages,
}: DidLatestTurnRunBulkVariantsParams): boolean => {
  if (!isSessionSettled) {
    return false;
  }

  const latestUserMessage = findLatestUserMessage(messages);

  if (!latestUserMessage) {
    return false;
  }

  if (!didTurnUseBulkVariants(messages, latestUserMessage)) {
    return false;
  }

  return !wasVariantTurnInterrupted(messages, latestUserMessage);
};
